import { mqttLogger } from '@hooks/mqtt/utils';
import { atom } from 'jotai';
import { manageListenerLifecycleAtom } from '../message/mqttListenerAtom';
import { SubscriptionConfig } from '../types';
import {
  logSubscriptionEvent,
  SubscriptionEvent,
  setDebugLabels,
} from '../utils/atomUtils';
import {
  ensureClientConnected,
  handleSubscriptionFailure,
  performMqttSubscription,
} from '../utils/mqttClientOperations';
import { validateTopic } from '../utils/topicUtils';
import { cancelDebouncedUnsubscribeAtom } from './debouncedUnsubscribeAtom';
import {
  addSubscriberAtom,
  removeSubscriberAtom,
  subscriberSetAtomFamily,
  subscriptionConfigAtomFamily,
  topicDataAtomFamily,
} from './subscriptionAtoms';

export const subscribeWithLifecycleAtom = atom(
  null,
  async (
    get,
    set,
    {
      topic,
      subscriberId,
      config,
    }: {
      topic: string;
      subscriberId: string;
      config?: SubscriptionConfig;
    },
  ) => {
    if (!validateTopic(topic, subscriberId)) {
      return;
    }

    const wasCancelled = set(cancelDebouncedUnsubscribeAtom, {
      topic,
      subscriberId,
    });

    if (wasCancelled) {
      mqttLogger.debug(
        `Cancelled pending unsubscribe and re-subscribing to ${topic} (subscriber: ${subscriberId})`,
      );
    }

    const client = await ensureClientConnected(get, set, topic);
    if (!client) {
      return;
    }

    const currentSubscribers = get(subscriberSetAtomFamily(topic));
    const isFirstSubscriber = currentSubscribers.size === 0;
    const isAlreadySubscribed = currentSubscribers.has(subscriberId);

    if (isAlreadySubscribed && !wasCancelled) {
      mqttLogger.debug(
        `Subscriber ${subscriberId} already subscribed to ${topic}`,
      );
      return;
    }

    set(manageListenerLifecycleAtom, 'increment');
    set(addSubscriberAtom, { topic, subscriberId });

    if (config) {
      set(subscriptionConfigAtomFamily(topic), config);
    }

    if (isFirstSubscriber) {
      try {
        await performMqttSubscription(client, topic, config);
        logSubscriptionEvent(SubscriptionEvent.Subscribe, topic);
      } catch (error) {
        set(removeSubscriberAtom, { topic, subscriberId });
        set(manageListenerLifecycleAtom, 'decrement');

        handleSubscriptionFailure(topic, error);
        set(topicDataAtomFamily(topic), null);

        throw error;
      }
    } else {
      logSubscriptionEvent(SubscriptionEvent.AddSubscriber, topic, {
        subscriberId,
      });
    }
  },
);

setDebugLabels({
  subscribeWithLifecycleAtom,
});
